<script lang="ts" setup>
    import { inject, reactive, onMounted } from 'vue';
    import { AccountAccess } from '@cwd/Account/Account.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import KeywordsList from '@shared/components/List.vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@cwd/shared/components/Form/Form';
    import Modal from '@shared/components/Modal.vue';
    import { type Keyword } from './TextMessagesTypes';
    import { loadKeywords, deleteKeyword, createKeyword } from './TextMessages.access';
    import { loadLists, type List } from '@views/Contact/Contact.access';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { bsAlert } from '@shared/components/bsAlerts';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const accountAccess: AccountAccess = injectorAccess.injector.get(AccountAccess);

    const state = reactive<{
        keywords: Keyword[];
        isBusy: boolean;
        selectedIds: string[];
        lists: List[];
        form: FormProps;
        openModal: boolean;
        setInitData: boolean;
    }>({
        keywords: [],
        isBusy: false,
        selectedIds: [],
        lists: [],
        form: {
            disableSubmit: false,
            alert: null,
            data: {
                Keyword: {
                    modelValue: '',
                    required: true,
                    group: $Resources.SmsAddNewKeywordLegend,
                    input: {
                        label: $Resources.SmsKeywordLabel,
                        maxlength: 8,
                    },
                },
                Lists: {
                    modelValue: [],
                    required: true,
                    isValid: undefined,
                    group: $Resources.SmsAddNewKeywordLegend,
                    multiSelect: {
                        label: $Resources.GenericListLabel,
                        selectProp: 'ListId',
                        displayProp: 'Name',
                        items: [],
                        loading: true,
                        maxSelectable: 1,
                        selectOnly: 'ListId',
                    },
                },
            },
        },
        openModal: false,
        setInitData: false,
    });

    onMounted(async () => {
        onLoad();

        state.form.data.Lists.multiSelect!.items = await loadLists();
        state.form.data.Lists.multiSelect!.loading = false;
    });

    const onLoad = (force?: true) => {
        if (state.isBusy && !force) return;
        state.isBusy = true;

        loadKeywords()
            .then((data) => {
                state.keywords = data;
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorTitleLabel, content: $Resources.ErrorLabel });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const deleteSelectedItems = () => {
        if (state.isBusy) return;
        state.isBusy = true;

        state.selectedIds.forEach((id: string, index: number) => {
            let keyword = state.keywords.find((k) => k.SmsKeywordId === id);
            keyword && onDelete(keyword, index + 1 === state.selectedIds.length);
        });
    };

    const onDelete = (keyword: Keyword, isLast: boolean) => {
        deleteKeyword(keyword.SmsKeywordId)
            .then(() => {
                keyword.Deleted = true;

                if (isLast) {
                    bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AlertSuccessBody });
                    clearSelection();
                }
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorTitleLabel, content: $Resources.ErrorLabel });
            })
            .finally(() => {
                if (isLast) {
                    state.isBusy = false;
                }
            });
    };

    const clearSelection = () => {
        state.selectedIds = [];
    };

    const onSubmit = (event: Event, formData: any) => {
        if (state.isBusy) return;
        state.isBusy = true;
        state.form.disableSubmit = true;

        createKeyword(formData.Keyword, formData.Lists[0])
            .then(() => {
                afterSubmit();
                bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AlertSuccessBody });
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorTitleLabel, content: $Resources.ErrorLabel });
            })
            .finally(() => {
                state.isBusy = false;
                state.form.disableSubmit = false;
            });
    };

    const afterSubmit = () => {
        state.form.data.Keyword.modelValue = '';
        state.form.data.Lists.modelValue = [];
        state.setInitData = true;
        state.openModal = false;
        onLoad(true);
    };

    const filterOptions = () => {
        return ['[Keyword]', '[ListName]'];
    };

    const openModal = () => {
        state.setInitData = false;
        state.openModal = true;
    };
</script>

<script lang="ts"></script>

<template>
    <!-- :navTitle="$Resources.SmsActiveKeywordHeader" -->

    <KeywordsList
        :title="$Resources.SmsKeyModalHeader"
        :description="$Resources.SmsKeywordInfotext.replace('{{smsAnswerCode}}', accountAccess.state.currentAccount!.SmsAnswerCode)"
        :checkedItems="state.selectedIds"
        itemId="SmsKeywordId"
        :items="state.keywords.filter((keyword) => !keyword.Deleted)"
        :loading="state.isBusy"
        :filterKeys="filterOptions()"
        @update:checkedItems="(newVal) => (state.selectedIds = newVal as string[])"
        @clearSelected="clearSelection"
        @deleteSelected="deleteSelectedItems">
        <template #toolbar>
            <div v-feature="'Sms.ReceiveResponses'">
                <button :disabled="state.isBusy" class="btn btn-default" v-show="state.selectedIds.length === 0" @click="openModal">
                    <span class="glyphicon glyphicon-plus m-xxs-r"></span>
                    {{ $Resources.SmsAddNewKeywordLegend }}
                </button>
            </div>
        </template>
        <template #item="{ item, index }">
            <div>
                <h3 class="h4">{{ item.Keyword }}</h3>
                <p
                    v-html="
                        $Resources.SmsKeywordListDescriptiveText.replace('{{smsAnswerCode}}', accountAccess.state.currentAccount!.SmsAnswerCode)
                            .replace('{{keyword}}', item.Keyword)
                            .replace('{{listId}}', item.ListId)
                            .replace('{{list}}', item.ListName)
                    "></p>
                <p class="meta">
                    <span class="glyphicon glyphicon-time"></span>
                    {{ formatDateTime(item.Created) }}
                    <span class="glyphicon glyphicon-user"></span>
                    {{ item.CreatedByUserName }}
                </p>
            </div>
        </template>
    </KeywordsList>

    <Modal :title="$Resources.SmsKeyModalHeader" :show="state.openModal" @close="state.openModal = false" :size="'sm'" :hideFooter="true">
        <template #content>
            <Form
                :buttonText="$Resources.AddButton"
                :data="state.form.data"
                :setInitData="state.setInitData"
                :alert="state.form.alert"
                :disableSubmit="state.form.disableSubmit"
                @update:isValid="(key, newVal) => (state.form!.data[key].isValid = newVal)"
                @update:modelValue="(key, newVal) => (state.form.data[key].modelValue = newVal)"
                @onSubmit="onSubmit"></Form>
        </template>
    </Modal>
</template>

<style></style>
